import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import "./landing.css";

export const Route = createFileRoute("/")({
  component: LandingPage,
});

type PublicStats = { total_users: number; individual_households: number; couple_households: number };

function useCountUp(target: number, duration = 1200) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target === 0) return;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(tick);
      else setValue(target);
    };
    requestAnimationFrame(tick);
  }, [target, duration]);
  return value;
}

function StatCounter({ value, label }: { value: number; label: string }) {
  const animated = useCountUp(value);
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", fontWeight: 800, color: "#C4A97A", lineHeight: 1 }}>
        {animated}
      </div>
      <div style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.45)", marginTop: "6px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
        {label}
      </div>
    </div>
  );
}

function LandingPage() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [stats, setStats] = useState<PublicStats | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        navigate({ to: "/dashboard" });
      } else {
        setAuthChecked(true);
      }
    });
    supabase.rpc("get_public_stats").then(({ data }) => {
      if (data) setStats(data as PublicStats);
    });
  }, [navigate]);

  useEffect(() => {
    if (!authChecked) return;
    document.documentElement.classList.add("landing-mode");
    document.body.classList.add("landing-mode");

    const fontLink = document.createElement("link");
    fontLink.rel = "preconnect";
    fontLink.href = "https://fonts.googleapis.com";
    fontLink.id = "landing-font-preconnect";

    const fontSheet = document.createElement("link");
    fontSheet.rel = "stylesheet";
    fontSheet.href = "https://fonts.googleapis.com/css2?family=Figtree:wght@300;400;600;800;900&display=swap";
    fontSheet.id = "landing-font-sheet";

    document.head.appendChild(fontLink);
    document.head.appendChild(fontSheet);

    return () => {
      document.documentElement.classList.remove("landing-mode");
      document.body.classList.remove("landing-mode");
      document.getElementById("landing-font-preconnect")?.remove();
      document.getElementById("landing-font-sheet")?.remove();
    };
  }, [authChecked]);

  useEffect(() => {
    if (!authChecked) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".l-fade-in").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [authChecked]);

  if (!authChecked) return null;

  function goToSignup() {
    navigate({ to: "/auth", search: { tab: "signup", invite_code: "" } });
  }

  function goToSignin() {
    navigate({ to: "/auth", search: { tab: "signin", invite_code: "" } });
  }

  return (
    <div className="l-landing" style={{ fontFamily: "'Figtree', sans-serif", background: "#000", color: "#fff", overflowX: "hidden", fontWeight: 300, lineHeight: 1.6 }}>

      {/* NAVBAR */}
      <nav className="l-nav">
        <a href="#" className="l-nav-logo">
          <img src="/Simbolo%20Dorado.svg" width="32" height="32" alt="El Patrón" />
          <span className="l-nav-logo-text">El Patrón de las finanzas</span>
        </a>
        <ul>
          <li><a href="#funciones">Funciones</a></li>
          <li><a href="#como-funciona">Cómo funciona</a></li>
          <li><a href="#precios">Precios</a></li>
          <li><a onClick={goToSignin} className="l-nav-signin" style={{cursor:"pointer"}}>Iniciar sesión</a></li>
          <li><a onClick={goToSignup} className="l-nav-cta" style={{cursor:"pointer"}}>Empezar gratis</a></li>
        </ul>
      </nav>

      {/* HERO */}
      <section className="l-hero">
        <div className="l-orb l-orb-gold" style={{width:"700px",height:"700px",top:"-200px",right:"-100px"}}></div>
        <div className="l-orb l-orb-amber" style={{width:"500px",height:"500px",bottom:"-100px",left:"-150px"}}></div>

        <div className="l-hero-badge">
          <span className="l-hero-badge-dot"></span>
          Decenas de parejas ya lo están usando
        </div>

        <h1>
          Las finanzas de tu hogar,<br /><span className="l-gold">sin excusas y sin peleas.</span>
        </h1>

        <p className="l-hero-sub">
          Una sola app para que vos y tu pareja vean el dinero de forma compartida, organizada y honesta — sin hojas de cálculo.
        </p>

        <div className="l-hero-actions">
          <button onClick={goToSignup} className="l-btn-primary">Empezar gratis →</button>
          <a href="#funciones" className="l-btn-ghost">Ver funciones</a>
        </div>
        <button onClick={goToSignin} className="l-hero-mobile-signin">
          ¿Ya tenés cuenta? <span>Iniciar sesión</span>
        </button>

        {/* DASHBOARD MOCKUP */}
        <div className="l-hero-mockup l-fade-in">
          <div className="l-mockup-bar">
            <div className="l-mockup-dot" style={{background:"#ff5f57"}}></div>
            <div className="l-mockup-dot" style={{background:"#febc2e"}}></div>
            <div className="l-mockup-dot" style={{background:"#28c840"}}></div>
          </div>
          <div className="l-mockup-content">
            <div className="l-mockup-card">
              <div className="l-mockup-card-label">Patrimonio neto</div>
              <div className="l-mockup-card-value green">$284,500</div>
              <div className="l-mockup-card-sub">+$4,200 este mes</div>
            </div>
            <div className="l-mockup-card">
              <div className="l-mockup-card-label">Presupuesto YTD</div>
              <div className="l-mockup-card-value gold">73%</div>
              <div className="l-mockup-card-sub">$42.800 de $58.600</div>
            </div>
            <div className="l-mockup-card">
              <div className="l-mockup-card-label">Balance entre ustedes</div>
              <div className="l-mockup-card-value red">- $3,200</div>
              <div className="l-mockup-card-sub">Ella le debe a él</div>
            </div>
            <div className="l-mockup-bar-chart">
              <div className="l-bar" style={{height:"40%",background:"rgba(150,127,84,0.3)"}}></div>
              <div className="l-bar" style={{height:"65%",background:"rgba(150,127,84,0.4)"}}></div>
              <div className="l-bar" style={{height:"55%",background:"rgba(150,127,84,0.35)"}}></div>
              <div className="l-bar" style={{height:"80%",background:"#967F54"}}></div>
              <div className="l-bar" style={{height:"70%",background:"rgba(150,127,84,0.45)"}}></div>
              <div className="l-bar" style={{height:"45%",background:"rgba(150,127,84,0.3)"}}></div>
              <div className="l-bar" style={{height:"60%",background:"rgba(150,127,84,0.4)"}}></div>
              <div className="l-bar" style={{height:"90%",background:"#C4A97A"}}></div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      {stats && (stats.total_users > 0) && (
        <section style={{ background: "var(--dark)", borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "48px 24px" }}>
          <div className="l-container" style={{ maxWidth: "720px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: "160px", padding: "0 24px" }}>
                <StatCounter value={stats.total_users} label="usuarios activos" />
              </div>
              <div style={{ width: "1px", background: "rgba(255,255,255,0.1)", height: "56px", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: "160px", padding: "0 24px" }}>
                <StatCounter value={stats.individual_households} label="hogares individuales" />
              </div>
              <div style={{ width: "1px", background: "rgba(255,255,255,0.1)", height: "56px", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: "160px", padding: "0 24px" }}>
                <StatCounter value={stats.couple_households} label="hogares en pareja" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* PROBLEMA */}
      <section className="l-section" style={{background:"var(--dark)",borderTop:"1px solid rgba(255,255,255,0.05)"}}>
        <div className="l-container">
          <div className="l-section-label">El problema</div>
          <h2 className="l-section-title">La mayoría de las parejas<br />discute de dinero. Todas las semanas.</h2>

          <div className="l-problem-grid">
            <div className="l-problem-card l-fade-in">
              <div className="l-problem-number">01</div>
              <h3 className="l-problem-title">Nadie sabe cuánto gastó el otro</h3>
              <p className="l-problem-desc">Llega fin de mes, los números no cierran, y empieza la discusión. Sin una vista compartida, la desconfianza llena el vacío de información.</p>
            </div>
            <div className="l-problem-card l-fade-in">
              <div className="l-problem-number">02</div>
              <h3 className="l-problem-title">Los presupuestos no duran más de un mes</h3>
              <p className="l-problem-desc">Hacés una planilla en Excel, funciona dos semanas, y nadie la actualiza. Los gastos con tarjeta se pierden, las transferencias no se registran.</p>
            </div>
            <div className="l-problem-card l-fade-in">
              <div className="l-problem-number">03</div>
              <h3 className="l-problem-title">No saben quién le debe a quién</h3>
              <p className="l-problem-desc">Ella pagó el super, él pagó el alquiler. ¿Cuánto se deben? Calcularlo es suficiente para arruinar una cena.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FUNCIONES */}
      <section className="l-section l-features-section" id="funciones">
        <div className="l-container">
          <div className="l-features-header">
            <div>
              <div className="l-section-label">Disponible hoy</div>
              <h2 className="l-section-title">Todo lo que necesitaban,<br />en un solo lugar.</h2>
            </div>
            <p className="l-section-desc">Cada función fue diseñada para parejas reales. Sin pasos de más, sin configuración imposible.</p>
          </div>

          <div className="l-features-grid">
            <div className="l-feature-card l-fade-in">
              <div className="l-feature-icon">
                <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 8h2a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2v4l-4-4H9a1.994 1.994 0 0 1-1.414-.586"/>
                  <path d="M3 2h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H9l-4 4v-4H3a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"/>
                </svg>
              </div>
              <h3 className="l-feature-title">División inteligente de gastos</h3>
              <p className="l-feature-desc">50/50, proporcional a los ingresos, o como prefieran. Cada gasto puede tener parte compartida y parte privada.</p>
            </div>

            <div className="l-feature-card l-fade-in">
              <div className="l-feature-icon">
                <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <line x1="8" y1="21" x2="16" y2="21"/>
                  <line x1="12" y1="17" x2="12" y2="21"/>
                </svg>
              </div>
              <h3 className="l-feature-title">Presupuestos conjuntos YTD</h3>
              <p className="l-feature-desc">Vista acumulada anual para saber si están en track. Los presupuestos de ambos en una sola pantalla, sin trabajo extra.</p>
            </div>

            <div className="l-feature-card l-fade-in">
              <div className="l-feature-icon">
                <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"/>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
              </div>
              <h3 className="l-feature-title">Balance en tiempo real</h3>
              <p className="l-feature-desc">Un número claro: quién le debe a quién y cuánto. Liquidación directa con un solo click.</p>
            </div>

            <div className="l-feature-card l-fade-in">
              <div className="l-feature-icon">
                <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <h3 className="l-feature-title">Privacidad por cuenta</h3>
              <p className="l-feature-desc">Elegís qué gastos son compartidos y cuáles son privados. Tu pareja ve que el saldo bajó, pero no en qué gastaste.</p>
            </div>

            <div className="l-feature-card l-fade-in">
              <div className="l-feature-icon">
                <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <h3 className="l-feature-title">Tarjetas de crédito</h3>
              <p className="l-feature-desc">Seguimiento de deuda, fechas de corte y vencimiento. Pagás la tarjeta como una transferencia interna.</p>
            </div>

            <div className="l-feature-card l-fade-in">
              <div className="l-feature-icon">
                <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10"/>
                  <line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="14"/>
                </svg>
              </div>
              <h3 className="l-feature-title">Dashboard de patrimonio</h3>
              <p className="l-feature-desc">Activos menos pasivos = tu número real. Una vista clara del estado financiero de tu hogar mes a mes.</p>
            </div>

            <div className="l-feature-card l-fade-in">
              <div className="l-feature-icon">
                <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </div>
              <h3 className="l-feature-title">Historial con filtros</h3>
              <p className="l-feature-desc">Buscá por mes, categoría o miembro del hogar. Todos los movimientos de ambos en un solo historial.</p>
            </div>

            <div className="l-feature-card l-fade-in">
              <div className="l-feature-icon">
                <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
              </div>
              <h3 className="l-feature-title">Multi-moneda</h3>
              <p className="l-feature-desc">Cuentas en pesos, dólares o lo que necesiten. Cada cuenta tiene su moneda y el tipo de cambio queda registrado.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRÓXIMAMENTE */}
      <section className="l-section" style={{background:"var(--black)"}}>
        <div className="l-container">
          <div className="l-section-label">Próximamente</div>
          <h2 className="l-section-title" style={{maxWidth:"600px"}}>La IA que habla de plata<br />como si fuera un amigo.</h2>

          <div className="l-coming-grid">
            <div className="l-coming-card featured">
              <div style={{position:"relative",zIndex:1}}>
                <div className="l-coming-badge">Próximamente</div>
                <h3 className="l-coming-title">El Patrón — Tu asistente financiero personal</h3>
                <p className="l-coming-desc" style={{marginBottom:"20px"}}>Una IA que analiza el historial de tu hogar y te da consejos en lenguaje natural. ¿En qué gastaron más este mes? ¿Van bien con el presupuesto anual? Preguntale directamente.</p>
                <p className="l-coming-desc">Para ella, el asistente se llama <strong style={{color:"var(--gold-light)"}}>La Jefa</strong>. Porque en casa mandan los dos.</p>
              </div>
              <div className="l-ai-preview">
                <div className="l-ai-msg">
                  <div className="l-ai-avatar">P</div>
                  <div className="l-ai-bubble">Hola. Este mes gastaron un 34% más de lo habitual en restaurantes. Si ajustan eso, podrían ahorrar $8.400 extras en lo que queda del año.</div>
                </div>
                <div className="l-ai-msg l-user-msg">
                  <div className="l-user-av">🤖</div>
                  <div className="l-user-bubble">¿Y en qué categoría vamos mejor que el mes pasado?</div>
                </div>
                <div className="l-ai-msg">
                  <div className="l-ai-avatar">P</div>
                  <div className="l-ai-bubble">En supermercado. Bajaron un 18% respecto al promedio de los últimos 3 meses. Bien ahí.</div>
                </div>
              </div>
            </div>

            <div className="l-coming-card l-fade-in">
              <div className="l-coming-badge">Próximamente</div>
              <h3 className="l-coming-title">Alertas predictivas de presupuesto</h3>
              <p className="l-coming-desc">La app te avisa antes de que rompan el presupuesto — no después. Con 5 días de anticipación, cuando todavía se puede ajustar.</p>
            </div>

            <div className="l-coming-card l-fade-in">
              <div className="l-coming-badge">Próximamente</div>
              <h3 className="l-coming-title">Score de salud financiera del hogar</h3>
              <p className="l-coming-desc">Un número entre 1 y 100 que resume cómo están financieramente. Basado en deuda, ahorro, presupuesto cumplido y tendencia mensual.</p>
            </div>

            <div className="l-coming-card l-fade-in">
              <div className="l-coming-badge">Próximamente</div>
              <h3 className="l-coming-title">Exportación de movimientos</h3>
              <p className="l-coming-desc">Descargá el historial completo en CSV o Excel. Para tu contador, para tu análisis propio, o simplemente para tener el control de tus datos.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section className="l-section l-how-section" id="como-funciona">
        <div className="l-container">
          <div style={{textAlign:"center",marginBottom:0}}>
            <div className="l-section-label" style={{justifyContent:"center"}}>Cómo funciona</div>
            <h2 className="l-section-title" style={{textAlign:"center"}}>Tres pasos y estás adentro.</h2>
          </div>
          <div className="l-steps">
            <div className="l-step l-fade-in">
              <div className="l-step-number">1</div>
              <h3 className="l-step-title">Creás tu hogar</h3>
              <p className="l-step-desc">Registrás tu cuenta, configurás tus cuentas bancarias y ponés los presupuestos por categoría que quieran manejar.</p>
            </div>
            <div className="l-step l-fade-in">
              <div className="l-step-number">2</div>
              <h3 className="l-step-title">Invitás a tu pareja</h3>
              <p className="l-step-desc">Le mandás un link. Ella o él se une al hogar y ven la misma información en tiempo real, cada uno desde su cuenta.</p>
            </div>
            <div className="l-step l-fade-in">
              <div className="l-step-number">3</div>
              <h3 className="l-step-title">Registran juntos</h3>
              <p className="l-step-desc">Cada uno carga sus movimientos. La app hace la magia: calcula quién gastó qué, el balance entre los dos, y cómo van respecto al presupuesto.</p>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="l-section" style={{background:"var(--black)"}}>
        <div className="l-container">
          <div className="l-section-label">Lo que dicen</div>
          <h2 className="l-section-title">Menos discusiones,<br />más claridad.</h2>
          <div className="l-testimonials-grid">
            <div className="l-testimonial l-fade-in">
              <div className="l-testimonial-quote">"</div>
              <p className="l-testimonial-text">Antes peleábamos cada fin de mes cuando llegaban las cuentas. Ahora <strong>lo vemos juntos en cinco minutos</strong> y ya está. Sin drama, sin sorpresas.</p>
              <div className="l-testimonial-author">
                <div className="l-testimonial-avatar">MS</div>
                <div>
                  <div className="l-testimonial-name">Mariana & Santiago</div>
                  <div className="l-testimonial-role">2 años usando Patrón</div>
                </div>
              </div>
            </div>
            <div className="l-testimonial l-fade-in">
              <div className="l-testimonial-quote">"</div>
              <p className="l-testimonial-text">El simple hecho de que los gastos sean visibles para los dos <strong>cambió la dinámica completamente</strong>. Ya no hay que adivinar, ya no hay que asumir.</p>
              <div className="l-testimonial-author">
                <div className="l-testimonial-avatar">LR</div>
                <div>
                  <div className="l-testimonial-name">Lucía & Rodrigo</div>
                  <div className="l-testimonial-role">Usando Patrón desde el beta</div>
                </div>
              </div>
            </div>
            <div className="l-testimonial l-fade-in">
              <div className="l-testimonial-quote">"</div>
              <p className="l-testimonial-text">Saber exactamente cuánto aporta cada uno y cuáles son gastos compartidos <strong>quitó una tensión enorme</strong>. Es como hablar del dinero sin hablar del dinero.</p>
              <div className="l-testimonial-author">
                <div className="l-testimonial-avatar">CP</div>
                <div>
                  <div className="l-testimonial-name">Camila & Pedro</div>
                  <div className="l-testimonial-role">Pareja + negocio compartido</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRECIOS */}
      <section className="l-section l-pricing-section" id="precios">
        <div className="l-container">
          <div className="l-pricing-header">
            <div className="l-section-label" style={{justifyContent:"center"}}>Precios</div>
            <h2 className="l-section-title" style={{textAlign:"center"}}>Empezá gratis,<br />crecé cuando quieras.</h2>
            <p className="l-section-desc" style={{textAlign:"center",margin:"0 auto",color:"rgba(255,255,255,0.4)"}}>Sin compromisos. Sin tarjeta de crédito para el plan gratis.</p>
          </div>

          <div className="l-pricing-grid">
            {/* Plan Gratis */}
            <div className="l-pricing-card">
              <p className="l-pricing-plan">Gratis</p>
              <div className="l-pricing-price">$0</div>
              <p className="l-pricing-desc">Para probar sin riesgo.</p>
              <div className="l-pricing-divider"></div>
              <ul className="l-pricing-features">
                <li><span className="l-check yes">✓</span>Hasta 100 movimientos totales</li>
                <li><span className="l-check yes">✓</span>Últimos 30 días de historial</li>
                <li><span className="l-check yes">✓</span>Hogar compartido con tu pareja</li>
                <li><span className="l-check yes">✓</span>Presupuestos por categoría</li>
                <li><span className="l-check yes">✓</span>Balance entre miembros</li>
                <li className="muted"><span className="l-check no">✗</span>Historial ilimitado</li>
                <li className="muted"><span className="l-check no">✗</span>El Patrón / La Jefa (IA)</li>
                <li className="muted"><span className="l-check no">✗</span>Alertas predictivas</li>
                <li className="muted"><span className="l-check no">✗</span>Score de salud financiera</li>
              </ul>
              <button onClick={goToSignup} className="l-btn-ghost" style={{display:"block",textAlign:"center",width:"100%"}}>Empezar gratis</button>
            </div>

            {/* Plan Pro */}
            <div className="l-pricing-card featured">
              <div className="l-pricing-popular">Más popular</div>
              <p className="l-pricing-plan" style={{color:"var(--gold-light)"}}>Pro</p>
              <div className="l-pricing-price" style={{color:"var(--white)"}}>
                <span>$</span>X<small>/mes</small>
              </div>
              <p className="l-pricing-desc">Para el hogar que va en serio.</p>
              <div className="l-pricing-divider" style={{background:"rgba(150,127,84,0.2)"}}></div>
              <ul className="l-pricing-features">
                <li><span className="l-check yes">✓</span>Movimientos ilimitados</li>
                <li><span className="l-check yes">✓</span>Historial completo sin límite</li>
                <li><span className="l-check yes">✓</span>Hogar compartido con tu pareja</li>
                <li><span className="l-check yes">✓</span>Presupuestos por categoría</li>
                <li><span className="l-check yes">✓</span>Balance entre miembros</li>
                <li><span className="l-check yes">✓</span>Multi-moneda avanzado</li>
                <li><span className="l-check yes" style={{background:"rgba(196,169,122,0.15)",color:"var(--gold-light)"}}>✓</span><span style={{color:"var(--gold-light)",fontWeight:600}}>El Patrón / La Jefa (IA) — próximo</span></li>
                <li><span className="l-check yes" style={{background:"rgba(196,169,122,0.15)",color:"var(--gold-light)"}}>✓</span><span style={{color:"var(--gold-light)",fontWeight:600}}>Alertas predictivas — próximo</span></li>
                <li><span className="l-check yes" style={{background:"rgba(196,169,122,0.15)",color:"var(--gold-light)"}}>✓</span><span style={{color:"var(--gold-light)",fontWeight:600}}>Score de salud financiera — próximo</span></li>
              </ul>
              <button onClick={goToSignup} className="l-btn-primary" style={{display:"block",textAlign:"center",width:"100%"}}>Empezar con Pro →</button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="l-cta-section">
        <div className="l-orb l-orb-gold" style={{width:"600px",height:"600px",top:"50%",left:"50%",transform:"translate(-50%,-50%)",opacity:0.2}}></div>
        <div style={{position:"relative",zIndex:1}}>
          <div className="l-diamond-divider">
            <svg width="32" height="32" viewBox="0 0 40 40" fill="none">
              <path d="M18 3 L33 18 L18 33 L3 18 Z" fill="none" stroke="rgba(150,127,84,0.4)" strokeWidth="1.5"/>
              <path d="M24 9 L39 24 L24 39 L9 24 Z" fill="#967F54" opacity="0.7"/>
            </svg>
          </div>
          <h2 className="l-section-title" style={{textAlign:"center",marginBottom:"16px"}}>
            El dinero no tiene que<br />ser motivo de pelea.
          </h2>
          <p className="l-section-desc" style={{textAlign:"center",margin:"0 auto 44px"}}>
            Empezá gratis hoy. Sin tarjeta de crédito, sin configuración larga.
          </p>
          <div style={{display:"flex",justifyContent:"center"}}>
            <button onClick={goToSignup} className="l-btn-primary" style={{fontSize:"17px",padding:"16px 40px"}}>Crear mi hogar gratis →</button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="l-footer">
        <a href="#" className="l-nav-logo">
          <img src="/Simbolo%20Dorado.svg" width="24" height="24" alt="El Patrón" />
          <span className="l-nav-logo-text" style={{fontSize:"15px"}}>El Patrón de las finanzas</span>
        </a>

        <ul className="l-footer-links">
          <li><a href="#">Privacidad</a></li>
          <li><a href="#">Términos</a></li>
          <li><a href="#">Contacto</a></li>
          <li><a href="https://finanzas.ivanrissopatron.com" target="_blank" rel="noreferrer">Ir a la app</a></li>
        </ul>

        <p className="l-footer-copy">© 2025 Iván Risso Patrón. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}
